// src/screens/mypage/NotificationsScreen.js
// 인앱 알림 목록 화면
// 수락 / 거절 / 신청 / 노쇼 제재 / 신고 처리 결과 알림을 Firestore notifications 컬렉션에서 조회
// 33차: noshow_penalty / report_result 타입 UI 추가
//        point_expiry / review_reminder / store_closed 타입 추가
// 51차: 색 theme.js 단일화 — C 팔레트를 COLORS 참조로 / 초록·빨강·주황·파랑 통일
// 72차: new_reservation/new_review 타입 추가 + 역할별 목록 필터링(매장관리자는 운영
//       알림만, 일반 회원은 고객용 알림만) — ADMIN_NOTIF_TYPES(recruitService.js) 기준
// 72차: reservation_cancelled_by_customer 타입 추가, 매장관리자 알림은 날짜별로 묶어서
//       표시. new_review 알림을 누르면 해당 리뷰 답글 화면(ReplyToReview)으로 이동
// 72차: digest_summary 타입 추가 — 알림 다이제스트(하루 요약) 기능용
// 74차: 화면 자체 커스텀 헤더 → 공통 AppHeader로 교체("모두 읽음" 버튼은 rightElement로 이동)
// 77차: 74차 교체 시 제목 누락("‹ 뒤로"만 보이던 것 — "AppHeader 없음" 반복 신고 원인) 복원
// 75차: favorite_event 알림 탭 시 Events로 이동하는데 showBack 파라미터가 없어 뒤로가기가
// 없던 버그 수정(HomeScreen.js의 동일 패턴 버그와 함께 발견)
// 78차: 헤더 "모두 읽음" 텍스트 버튼을 공용 TextButton으로 교체

import React, { useState, useCallback, useRef } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, RefreshControl,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useAuth } from '../../contexts/AuthContext';
import { getMyNotifications, markNotificationRead, ADMIN_NOTIF_TYPES } from '../../services/recruitService';
import { COLORS } from '../../constants/theme';
import { toLocalYMD, todayStr as localTodayStr } from '../../utils/dateUtils';
import AppHeader from '../../components/common/AppHeader';
import { TextButton } from '../../components/common/Buttons';
import {
  EmptyNotificationsIcon, MenuStoreIcon, PriceIcon, MenuReviewIcon, RewardIcon,
  MenuReservationIcon, StatsIcon, MenuInquiryIcon, MenuBellIcon,
  ReservationCancelIcon, ParticipantAcceptIcon, ReceiptIcon, ClockIcon,
  RecruitRejectedIcon, RecruitAppliedIcon, NoShowPenaltyIcon, OverdueIcon,
  ContractExpiringIcon, ManualEntryReminderIcon, CrewMessageIcon, RefreshIcon,
} from '../../components/common/Icons';

const C = {
  bg: COLORS.bg, surface: COLORS.surface, card: COLORS.surface2,
  border: COLORS.border, gold: COLORS.gold,
  text: COLORS.text, textSec: 'rgba(255,255,255,0.6)',
  muted: COLORS.textMuted, success: COLORS.green,
  danger: COLORS.red, warning: COLORS.orange, info: COLORS.blue,
};

const TYPE_CONFIG = {
  recruit_approved: {
    emoji: '🎉', Icon: ParticipantAcceptIcon,
    color: C.success,
    label: '참가 수락',
  },
  recruit_rejected: {
    emoji: '😢', Icon: RecruitRejectedIcon,
    color: C.danger,
    label: '참가 거절',
  },
  recruit_applied: {
    emoji: '📬', Icon: RecruitAppliedIcon,
    color: C.warning,
    label: '참가 신청',
  },
  // 80차 후속: 모집 작성자가 그룹 예약을 취소했을 때 참가자에게 가는 알림
  recruit_bulk_cancelled: {
    emoji: '❌', Icon: ReservationCancelIcon,
    color: C.danger,
    label: '예약 취소',
  },
  // 80차 계속: 참여인원 자리 양도 완료(나가는 사람에게)
  recruit_seat_transferred: {
    emoji: '🔄', Icon: RefreshIcon,
    color: C.info,
    label: '자리 양도',
  },
  // 80차 계속: 관심 지역·장르 알림
  recruit_genre_match: {
    emoji: '🔔', Icon: MenuBellIcon,
    color: C.gold,
    label: '관심 모집글',
  },
  // 80차 계속: 내 모집글에 새 댓글(작성자 재방문 유도)
  recruit_comment: {
    emoji: '💬', Icon: MenuInquiryIcon,
    color: C.info,
    label: '새 댓글',
  },
  // 80차 계속: 크루 메시지 @멘션/@전체 호출
  crew_mention: {
    emoji: '📣', Icon: CrewMessageIcon,
    color: C.info,
    label: '크루 호출',
  },
  // 80차 계속: 친구 초대 성공(초대자에게)
  referral_success: {
    emoji: '🎁', Icon: RewardIcon,
    color: C.success,
    label: '친구 초대',
  },
  // 80차 계속: 휴면 유저 재참여 넛지
  dormant_nudge: {
    emoji: '📬', Icon: RecruitAppliedIcon,
    color: C.gold,
    label: '모집 게시판',
  },
  // 33차: 노쇼 제재 타입
  noshow_penalty: {
    emoji: '⚠️', Icon: NoShowPenaltyIcon,
    color: C.danger,
    label: '노쇼 제재',
  },
  // 33차: 신고 처리 결과 타입
  report_result: {
    emoji: '🔔', Icon: MenuBellIcon,
    color: C.info,
    label: '신고 처리',
  },
  // 33차 단기 UX
  point_expiry: {
    emoji: '💎', Icon: RewardIcon,
    color: C.warning,
    label: '포인트 소멸',
  },
  review_reminder: {
    emoji: '⭐', Icon: MenuReviewIcon,
    color: C.success,
    label: '리뷰 작성',
  },
  store_closed: {
    emoji: '🏪', Icon: MenuStoreIcon,
    color: C.muted,
    label: '매장 안내',
  },
  favorite_event: {
    emoji: '🔔', Icon: MenuBellIcon,
    color: C.gold,
    label: '즐겨찾기 이벤트',
  },
  // 78차: 즐겨찾기 알림(notify 토글) 대상 확장 — 재개장/가격변경도 같은 섹션에서 다룸
  favorite_reopen: {
    emoji: '🏪', Icon: MenuStoreIcon,
    color: C.gold,
    label: '재개장 소식',
  },
  favorite_price_change: {
    emoji: '💰', Icon: PriceIcon,
    color: C.gold,
    label: '가격 변경',
  },
  // 72차: 매장관리자 전용 알림 타입
  new_reservation: {
    emoji: '📋', Icon: MenuReservationIcon,
    color: C.success,
    label: '새 예약',
  },
  new_review: {
    emoji: '✍️', Icon: MenuReviewIcon,
    color: C.gold,
    label: '새 리뷰',
  },
  reservation_cancelled_by_customer: {
    emoji: '❌', Icon: ReservationCancelIcon,
    color: C.danger,
    label: '예약 취소',
  },
  digest_summary: {
    emoji: '📊', Icon: StatsIcon,
    color: C.gold,
    label: '일일 요약',
  },
  // 75차: 문의 답변 알림 — 기존엔 이 타입 자체가 없어서(답변 알림 자체가 없었음) 추가
  inquiry_replied: {
    emoji: '💬', Icon: MenuInquiryIcon,
    color: C.info,
    label: '문의 답변',
  },
  // 78차 66차: 고객이 매장에 문의를 남기면 담당 매장관리자에게 오는 알림 — 기존엔
  // 이 타입 자체가 없어(onNewInquiry가 targetType:'app'만 다룸) 매장관리자가 새 고객
  // 문의를 알 방법이 전혀 없었음.
  new_store_inquiry: {
    emoji: '💬', Icon: MenuInquiryIcon,
    color: C.info,
    label: '고객 문의',
  },
  // 79차: [버그 수정] 관리자가 답변한 뒤 문의자가 후속 메시지를 남겨도(재오픈) 담당
  // 관리자에게 알릴 방법이 전혀 없었음(fork로 발견) — onInquiryReplied가 role==='user'
  // 케이스도 다루도록 확장하며 신규 타입 추가.
  inquiry_followup: {
    emoji: '💬', Icon: MenuInquiryIcon,
    color: C.info,
    label: '문의 추가 메시지',
  },
  // 75차: 총괄관리자용 — 3일 넘게 미처리된 신고 요약 알림(자동 제재/기각은 하지 않음,
  // report_result와는 다른 타입 — report_result는 "피신고자"에게 가는 처리 결과 알림)
  report_overdue: {
    emoji: '⏰', Icon: OverdueIcon,
    color: C.danger,
    label: '신고 미처리',
  },
  // 78차 39차: 매월 자동 정산서(인보이스) 생성 안내(매장관리자 전용) — scheduledMonthlySettlement
  monthly_invoice_ready: {
    emoji: '🧾', Icon: ReceiptIcon,
    color: C.gold,
    label: '정산 인보이스',
  },
  // 78차 32차: 매장 계약 만료 임박 안내(총관리자 전용) — scheduledContractExpiryReminder
  contract_expiring: {
    emoji: '📋', Icon: ContractExpiringIcon,
    color: C.warning,
    label: '계약 만료 안내',
  },
  // 78차: 외부채널 예약 수동입력 리마인드(매장관리자 전용) — scheduledManualEntryReminder
  manual_entry_reminder: {
    emoji: '📝', Icon: ManualEntryReminderIcon,
    color: C.info,
    label: '예약 입력 안내',
  },
  // 78차 39차: 전수조사로 발견 — 서버(functions/index.js)가 실제로 만드는 타입 3종이
  // 여기 없어서 전용 아이콘/라벨 없이 종모양(🔔 "알림")으로만 뭉뚱그려 보이고 있었음.
  // 74차: 예약 24시간/3시간 전 리마인드 — scheduledReservationReminder
  reservation_reminder: {
    emoji: '⏰', Icon: ClockIcon,
    color: C.info,
    label: '예약 안내',
  },
  // 75차: 새 앱 문의(총괄관리자 전용) — onNewInquiry
  new_inquiry: {
    emoji: '💬', Icon: MenuInquiryIcon,
    color: C.info,
    label: '새 문의',
  },
  // 매장이 예약을 취소했을 때 고객에게 가는 알림 — onReservationCancelled(고객→매장, 위
  // reservation_cancelled_by_customer)와 반대 방향.
  reservation_cancelled_by_store: {
    emoji: '❌', Icon: ReservationCancelIcon,
    color: C.danger,
    label: '예약 취소 안내',
  },
};

// 33차: 타입별 상세 렌더링 컴포넌트
function NotifDetail({ item }) {
  if (item.type === 'noshow_penalty') {
    return (
      <View style={d.penaltyBox}>
        <View style={d.penaltyRow}>
          <Text style={d.penaltyLabel}>제재 유형</Text>
          <Text style={d.penaltyValue}>{item.banLabel || '모집 참가 제한'}</Text>
        </View>
        {item.noShowCount != null && (
          <View style={d.penaltyRow}>
            <Text style={d.penaltyLabel}>누적 노쇼</Text>
            <Text style={[d.penaltyValue, { color: C.danger }]}>{item.noShowCount}회</Text>
          </View>
        )}
        {item.banUntil && item.banUntil !== '9999-12-31T00:00:00.000Z' && (
          <View style={d.penaltyRow}>
            <Text style={d.penaltyLabel}>해제 일시</Text>
            <Text style={d.penaltyValue}>
              {new Date(item.banUntil).toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            </Text>
          </View>
        )}
        {item.banUntil === '9999-12-31T00:00:00.000Z' && (
          <View style={d.penaltyRow}>
            <Text style={d.penaltyLabel}>해제 일시</Text>
            <Text style={[d.penaltyValue, { color: C.danger }]}>영구 정지</Text>
          </View>
        )}
        <View style={d.csRow}>
          <Text style={d.csTxt}>이의 신청은 고객센터로 문의해주세요.</Text>
        </View>
      </View>
    );
  }

  if (item.type === 'report_result') {
    const isResolved = item.resultType === 'resolved';
    return (
      <View style={[d.reportBox, { borderColor: isResolved ? C.danger + '40' : C.info + '40' }]}>
        <View style={d.penaltyRow}>
          <Text style={d.penaltyLabel}>처리 결과</Text>
          <View style={[d.resultBadge, { backgroundColor: isResolved ? C.danger + '18' : C.info + '18' }]}>
            <Text style={[d.resultBadgeTxt, { color: isResolved ? C.danger : C.info }]}>
              {isResolved ? '신고 처리됨' : '신고 기각됨'}
            </Text>
          </View>
        </View>
        {item.reportReason && (
          <View style={d.penaltyRow}>
            <Text style={d.penaltyLabel}>신고 사유</Text>
            <Text style={d.penaltyValue} numberOfLines={2}>{item.reportReason}</Text>
          </View>
        )}
        {isResolved && (
          <View style={d.csRow}>
            <Text style={d.csTxt}>커뮤니티 가이드라인을 준수해 주세요.</Text>
          </View>
        )}
      </View>
    );
  }

  return null;
}

// 72차: 매장관리자 알림을 날짜별로 묶어 보여주기 위한 헬퍼
// 78차: toISOString()(UTC)이던 것을 전부 로컬 기준으로 교체 — 한국 새벽 0~9시엔 오늘 받은
// 알림이 "어제" 그룹으로 잘못 묶여 보이던 버그.
function dateSortKey(ts) {
  if (!ts) return '';
  try { return toLocalYMD(ts.toDate ? ts.toDate() : new Date(ts)); }
  catch { return ''; }
}
function dateGroupLabel(ts) {
  if (!ts) return '날짜 없음';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const key = toLocalYMD(d);
    const today = localTodayStr();
    const yesterday = (() => { const y = new Date(); y.setDate(y.getDate() - 1); return toLocalYMD(y); })();
    if (key === today) return '오늘';
    if (key === yesterday) return '어제';
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  } catch { return '날짜 없음'; }
}

function formatDate(ts) {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1)    return '방금 전';
    if (diff < 60)   return `${diff}분 전`;
    if (diff < 1440) return `${Math.floor(diff / 60)}시간 전`;
    return `${Math.floor(diff / 1440)}일 전`;
  } catch { return ''; }
}

export default function NotificationsScreen() {
  const navigation = useNavigation();
  const { user, profile } = useAuth();
  // 72차: 매장관리자 계정은 새 예약/새 리뷰 등 운영 알림만, 일반 회원은 기존 고객용
  // 알림만 보이도록 분리 — 매장관리자가 리뷰 유도(review_reminder) 등 무관한 고객 알림을
  // 보게 되던 문제 수정.
  const isStoreAdmin = profile?.adminRole === 'store';
  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [expanded,      setExpanded]      = useState({});

  // 79차: 화면을 빠르게 나갔다 다시 들어오면(알림 탭→다른 화면→복귀) 이전 포커스의
  // load()가 늦게 응답해 최신 목록을 오래된 것으로 덮어쓸 수 있었음(MyReviewsScreen.js가
  // 74차에 이미 이 패턴으로 막아둔 것과 동일한 위험, 이 화면엔 빠져있었음) — activeRef로
  // 이전 포커스 세션의 setState만 무시.
  const activeRef = useRef(true);
  const load = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const list = await getMyNotifications(user.uid);
      if (!activeRef.current) return;
      const filtered = isStoreAdmin
        ? list.filter(n => ADMIN_NOTIF_TYPES.includes(n.type))
        : list.filter(n => !ADMIN_NOTIF_TYPES.includes(n.type));
      setNotifications(filtered);
    } catch (e) { if (activeRef.current) console.error(e); }
    finally { if (activeRef.current) setLoading(false); }
  }, [user?.uid, isStoreAdmin]);

  useFocusEffect(useCallback(() => {
    activeRef.current = true;
    load();
    return () => { activeRef.current = false; };
  }, [load]));

  const handlePress = async (item) => {
    if (!item.read) {
      await markNotificationRead(item.id).catch(() => {});
      setNotifications(prev =>
        prev.map(n => n.id === item.id ? { ...n, read: true } : n)
      );
    }
    // 노쇼·신고 타입은 토글 상세 표시(NotifDetail이 실제 내용을 그림)
    if (item.type === 'noshow_penalty' || item.type === 'report_result') {
      setExpanded(prev => ({ ...prev, [item.id]: !prev[item.id] }));
      return;
    }
    // 79차: [버그 수정] store_closed가 위 "펼치기" 그룹에 같이 묶여 있었는데 NotifDetail은
    // 이 타입에 대해 아무 것도 렌더하지 않아서(펼쳐도 빈 화면), 탭해도 사실상 아무 반응이
    // 없는 것처럼 보였음 — App.js의 콜드스타트 케이스는 이미 'ReservationHistory'로 이동
    // 시키는데 인앱(이 화면)에서 탭할 땐 그 이동이 아예 없었던 것(전수 정독으로 발견).
    if (item.type === 'store_closed') {
      navigation.navigate('ReservationHistory');
      return;
    }
    // 1번: 리뷰 유도 알림 → 내 리뷰 화면(작성 가능 탭)으로 이동
    if (item.type === 'review_reminder') {
      navigation.navigate('MyReviews');
      return;
    }
    // 72차: 새 리뷰 알림 → 해당 리뷰에 바로 답글을 달 수 있는 화면으로 이동
    if (item.type === 'new_review' && item.reviewId) {
      navigation.navigate('ReplyToReview', { reviewId: item.reviewId });
      return;
    }
    // 포인트 소멸 알림 → 포인트 내역 화면으로 이동
    if (item.type === 'point_expiry') {
      navigation.navigate('PointHistory');
      return;
    }
    // 즐겨찾기 이벤트 알림 → 공지/소식 탭으로 이동
    if (item.type === 'favorite_event') {
      navigation.navigate('Events', { showBack: true });
      return;
    }
    // 78차: 가격변경은 특정 테마 하나에 대한 알림이라 그 테마 상세로, 재개장은 매장
    // 전체가 다시 열린 소식이라 매장 상세로 이동
    if (item.type === 'favorite_price_change' && item.productId) {
      navigation.navigate('ThemeDetail', { product: { id: item.productId } });
      return;
    }
    if (item.type === 'favorite_reopen' && item.storeId) {
      navigation.navigate('StoreDetail', { storeId: item.storeId });
      return;
    }
    // 78차 66차: 새 예약/고객 취소 알림 → 운영관리(예약 관리) 탭으로 바로 이동
    // (기존엔 아무 동작도 안 하고 읽음 처리만 되던 것 — 매장관리자가 알림을 눌러도
    // 직접 예약 관리 탭을 찾아 들어가야 했음)
    if (item.type === 'new_reservation' || item.type === 'reservation_cancelled_by_customer') {
      navigation.navigate('Main', { screen: 'StoreAdminOps' });
      return;
    }
    // 78차 66차: 고객 문의/앱 문의 알림 → 답변할 수 있는 화면으로 바로 이동
    if (item.type === 'new_store_inquiry') {
      navigation.navigate('Inquiries', { mode: 'store' });
      return;
    }
    if (item.type === 'new_inquiry') {
      navigation.navigate('Inquiries', { mode: 'app' });
      return;
    }
    // 79차: 문의자 후속 메시지 알림 → storeId 유무로 store/app 문의 구분해 이동
    if (item.type === 'inquiry_followup') {
      navigation.navigate('Inquiries', { mode: item.storeId ? 'store' : 'app' });
      return;
    }
    // 78차 68차: 내 문의(앱/매장)에 답변이 달렸다는 알림 — 지금까지 이 타입 자체를
    // 처리하는 분기가 없어서 눌러도 아무 데도 안 가고 읽음 처리만 되던 버그
    // (일반 회원이 가장 흔하게 받는 알림 타입 중 하나인데 유일하게 빠져 있었음).
    if (item.type === 'inquiry_replied') {
      navigation.navigate('MyInquiries');
      return;
    }
    // 79차: [버그 수정] 아래 6개 타입 전부 App.js의 콜드스타트 딥링크 switch엔 이미
    // 케이스가 있는데(여러 라운드에 걸쳐 추가돼 옴), 이 화면(인앱에서 알림 목록을 열어
    // 직접 탭하는 "웜" 경로)엔 대응하는 처리가 하나도 없어서 앱을 이미 켜둔 채로 이
    // 알림들을 탭하면 읽음 처리만 되고 아무 데도 이동하지 않았음(전수 정독으로 발견,
    // App.js 쪽만 여러 번 보완되고 이 화면은 그 짝을 놓친 형태). App.js와 동일한
    // 이동 대상으로 맞춤.
    if (item.type === 'reservation_reminder' || item.type === 'reservation_cancelled_by_store') {
      navigation.navigate('ReservationHistory');
      return;
    }
    if (item.type === 'digest_summary' || item.type === 'manual_entry_reminder' || item.type === 'monthly_invoice_ready') {
      navigation.navigate('Main');
      return;
    }
    if (item.type === 'contract_expiring') {
      navigation.navigate('Main', { screen: 'SuperStores' });
      return;
    }
    if (item.type === 'report_overdue') {
      navigation.navigate('Main', { screen: 'SuperUserReport' });
      return;
    }
    // 80차 계속: 크루 메시지 @멘션/@전체 호출 — App.js 콜드스타트 딥링크와 동일 대상.
    if (item.type === 'crew_mention') {
      navigation.navigate('Main', { screen: 'Crew' });
      return;
    }
    // 80차 계속: 친구 초대 성공 — App.js 콜드스타트 딥링크와 동일 대상(재공유 유도).
    if (item.type === 'referral_success') {
      navigation.navigate('InviteFriend');
      return;
    }
    // 80차 계속: 휴면 유저 재참여 넛지 — App.js 콜드스타트 딥링크와 동일 대상.
    if (item.type === 'dormant_nudge') {
      navigation.navigate('Main', { screen: 'Recruit' });
      return;
    }
    if (item.recruitId) {
      navigation.navigate('RecruitDetail', { recruitId: item.recruitId });
    }
  };

  const handleMarkAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    await Promise.all(unread.map(n => markNotificationRead(n.id).catch(() => {})));
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // 33차: 타입 그룹별 섹션 분리 렌더링 (일반 회원용)
  const penaltyAndReport = notifications.filter(
    n => n.type === 'noshow_penalty' || n.type === 'report_result' || n.type === 'store_closed'
  );
  const favoriteEvents = notifications.filter(
    n => n.type === 'favorite_event'
  );
  const general = notifications.filter(
    n => !['noshow_penalty', 'report_result', 'store_closed', 'favorite_event'].includes(n.type)
  );

  // 72차: 매장관리자용 — 예약완료/예약취소/새 리뷰를 날짜별로 묶어서 렌더
  const dateGroups = isStoreAdmin ? (() => {
    const groups = {};
    notifications.forEach(n => {
      const key = dateSortKey(n.createdAt);
      if (!groups[key]) groups[key] = { label: dateGroupLabel(n.createdAt), items: [] };
      groups[key].items.push(n);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  })() : [];

  const renderCard = (item) => {
    const cfg = TYPE_CONFIG[item.type] || { emoji: '🔔', color: C.gold, label: '알림' };
    // 79차: store_closed는 NotifDetail이 실제로 아무 내용도 안 그려서 "펼치기" 힌트 자체가
    // 오해 소지였음(위 handlePress 수정과 짝) — 상세보기 대상에서 제외.
    const isExpandable = item.type === 'noshow_penalty' || item.type === 'report_result';
    const isExpanded = expanded[item.id];

    return (
      <TouchableOpacity
        key={item.id}
        style={[s.card, !item.read && s.cardUnread]}
        onPress={() => handlePress(item)}
        activeOpacity={0.75}>
        {!item.read && <View style={s.unreadDot} />}

        <View style={[s.iconBox, {
          backgroundColor: cfg.color + '18',
          borderColor: cfg.color + '40',
        }]}>
          {cfg.Icon ? <cfg.Icon size={20} color={cfg.color} /> : <Text style={{ fontSize: 22 }}>{cfg.emoji}</Text>}
        </View>

        <View style={{ flex: 1, gap: 3 }}>
          {/* 타입 레이블 뱃지 */}
          <View style={[s.typeBadge, { backgroundColor: cfg.color + '15', borderColor: cfg.color + '35' }]}>
            <Text style={[s.typeBadgeTxt, { color: cfg.color }]}>{cfg.label}</Text>
          </View>

          <Text style={s.notifTitle}>{item.title}</Text>
          <Text style={s.notifBody} numberOfLines={isExpanded ? undefined : 2}>
            {item.body}
          </Text>

          {/* 33차: 상세 정보 펼치기 */}
          {isExpandable && isExpanded && <NotifDetail item={item} />}

          <View style={s.notifFooter}>
            <Text style={s.notifTime}>{formatDate(item.createdAt)}</Text>
            {isExpandable && (
              <Text style={[s.expandHint, { color: cfg.color }]}>
                {isExpanded ? '접기 ▲' : '상세 보기 ▼'}
              </Text>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={s.container}>
      <AppHeader
        showBack
        centerElement={<Text style={{ color: COLORS.text, fontSize: 15, fontWeight: '800' }}>알림</Text>}
        rightElement={
          // 78차 38차: TextButton 자체가 alignSelf:'flex-start'라 AppHeader의 right 컬럼
          // (flex:1, alignItems:flex-end) 안에서도 칼럼의 왼쪽(=헤더 중앙 쪽)에 붙어 보이던
          // 버그 — LoginScreen.js의 guestBtn과 동일한 원인.
          unreadCount > 0 ? (
            <TextButton label="모두 읽음" onPress={handleMarkAllRead} textStyle={s.markAllBtn} style={{ alignSelf: 'flex-end' }} />
          ) : null
        }
      />

      {loading ? (
        <View style={s.center}>
          <ActivityIndicator size="large" color={C.gold} />
        </View>
      ) : (
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={s.body}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={C.gold} />
          }>

          {notifications.length === 0 ? (
            <View style={s.emptyBox}>
              <EmptyNotificationsIcon size={64} />
              <Text style={[s.emptyTxt, { marginTop: 12 }]}>알림이 없습니다</Text>
              <Text style={s.emptySub}>
                {isStoreAdmin
                  ? '새 예약·예약 취소·새 리뷰 알림이 여기에 표시됩니다'
                  : '모집·예약·포인트·즐겨찾기 이벤트·제재 알림이 여기에 표시됩니다'}
              </Text>
            </View>
          ) : isStoreAdmin ? (
            // 72차: 매장관리자는 날짜별로 묶어서 표시 (예약완료/예약취소/새 리뷰)
            <>
              {dateGroups.map(([key, group]) => (
                <View key={key}>
                  <View style={s.sectionLabel}>
                    <View style={s.sectionBar} />
                    <Text style={s.sectionTxt}>{group.label}</Text>
                  </View>
                  {group.items.map(renderCard)}
                </View>
              ))}
            </>
          ) : (
            <>
              {/* 제재·신고 알림 섹션 */}
              {penaltyAndReport.length > 0 && (
                <>
                  <View style={s.sectionLabel}>
                    <View style={s.sectionBar} />
                    <Text style={s.sectionTxt}>제재 · 신고 · 안내</Text>
                  </View>
                  {penaltyAndReport.map(renderCard)}
                </>
              )}

              {/* 즐겨찾기 이벤트 섹션 */}
              {favoriteEvents.length > 0 && (
                <>
                  <View style={s.sectionLabel}>
                    <View style={[s.sectionBar, { backgroundColor: C.gold }]} />
                    <Text style={s.sectionTxt}>즐겨찾기 이벤트</Text>
                  </View>
                  {favoriteEvents.map(renderCard)}
                </>
              )}

              {/* 일반 알림 섹션 */}
              {general.length > 0 && (
                <>
                  {(penaltyAndReport.length > 0 || favoriteEvents.length > 0) && (
                    <View style={s.sectionLabel}>
                      <View style={s.sectionBar} />
                      <Text style={s.sectionTxt}>모집 · 기타 알림</Text>
                    </View>
                  )}
                  {general.map(renderCard)}
                </>
              )}
            </>
          )}
        </ScrollView>
      )}
    </View>
  );
}

const d = StyleSheet.create({
  penaltyBox: {
    marginTop: 10,
    backgroundColor: C.danger + '0F',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: C.danger + '40',
    padding: 10,
    gap: 6,
  },
  reportBox: {
    marginTop: 10,
    backgroundColor: C.info + '0F',
    borderRadius: 10,
    borderWidth: 1,
    padding: 10,
    gap: 6,
  },
  penaltyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  penaltyLabel: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 11,
    fontWeight: '600',
    width: 56,
    flexShrink: 0,
  },
  penaltyValue: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    fontWeight: '600',
    flex: 1,
    textAlign: 'right',
  },
  resultBadge: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  resultBadgeTxt: {
    fontSize: 11,
    fontWeight: '700',
  },
  csRow: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
    paddingTop: 6,
    marginTop: 2,
  },
  csTxt: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 11,
    lineHeight: 16,
  },
});

const s = StyleSheet.create({
  container:   { flex: 1, backgroundColor: C.bg },
  center:      { flex: 1, justifyContent: 'center', alignItems: 'center' },
  markAllBtn:  { color: C.textSec, fontSize: 13, fontWeight: '600' },
  body:        { paddingVertical: 12, paddingHorizontal: 16, gap: 8, paddingBottom: 40 },
  emptyBox:    { flex: 1, alignItems: 'center', paddingTop: 80 },
  emptyTxt:    { color: C.text, fontSize: 16, fontWeight: '700', marginBottom: 6 },
  emptySub:    { color: C.muted, fontSize: 13, textAlign: 'center' },

  sectionLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    marginBottom: 2,
  },
  sectionBar:   { width: 3, height: 13, backgroundColor: C.gold, borderRadius: 2 },
  sectionTxt:   { color: C.gold, fontSize: 12, fontWeight: '700', letterSpacing: 0.3 },

  card:        {
    flexDirection: 'row', alignItems: 'flex-start', gap: 12,
    backgroundColor: C.card, borderRadius: 14, borderWidth: 1,
    borderColor: C.border, padding: 14,
  },
  cardUnread:  {
    borderColor: 'rgba(212,168,67,0.35)',
    backgroundColor: 'rgba(212,168,67,0.04)',
  },
  unreadDot:   {
    position: 'absolute', top: 12, right: 12,
    width: 8, height: 8, borderRadius: 4, backgroundColor: C.gold,
  },
  iconBox:     {
    width: 46, height: 46, borderRadius: 12, borderWidth: 1,
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },

  typeBadge:    {
    alignSelf: 'flex-start',
    borderRadius: 6, borderWidth: 1,
    paddingHorizontal: 7, paddingVertical: 2,
    marginBottom: 2,
  },
  typeBadgeTxt: { fontSize: 10, fontWeight: '700' },

  notifTitle:  { color: C.text, fontSize: 14, fontWeight: '700', lineHeight: 20 },
  notifBody:   { color: C.textSec, fontSize: 13, lineHeight: 18 },
  notifFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 },
  notifTime:   { color: C.muted, fontSize: 11 },
  expandHint:  { fontSize: 11, fontWeight: '600' },
});